import React, { useEffect, useState } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { returnTool, reportToolLost } from '../../../services/toolAssignmentService';
import { uploadInventoryImage } from '../../../services/inventoryService';
import DestructiveConfirmModal from '../../../components/common/DestructiveConfirmModal';
import ToolPhotoInput from './ToolPhotoInput';

const CONDITION_OPTIONS = [
  { value: 'good', label: 'En buen estado' },
  { value: 'damaged', label: 'Dañada' },
  { value: 'maintenance', label: 'Necesita mantenimiento' },
  { value: 'lost', label: 'Pérdida parcial / problemas serios' }
];

const ToolReturnModal = ({ show, onHide, assignment, mode = 'return' }) => {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');
  const [returnCondition, setReturnCondition] = useState('good');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (show) {
      setNotes('');
      setReturnCondition('good');
      setPhotoFile(null);
      setPhotoPreview(null);
      setError('');
    }
  }, [show]);

  const needsPhoto = returnCondition !== 'good';
  const photoOk = !needsPhoto || !!photoFile;

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['tools'] });
    queryClient.invalidateQueries({ queryKey: ['all-tools'] });
    queryClient.invalidateQueries({ queryKey: ['active-assignments'] });
    queryClient.invalidateQueries({ queryKey: ['my-assignments'] });
    queryClient.invalidateQueries({ queryKey: ['tools-available'] });
    queryClient.invalidateQueries({ queryKey: ['tool-detail'] });
  };

  const returnMutation = useMutation({
    mutationFn: async () => {
      let return_photo_url = null;
      if (photoFile && assignment?.id) {
        setUploading(true);
        const upload = await uploadInventoryImage(photoFile, `returns/${assignment.id}`);
        setUploading(false);
        if (upload.error) throw upload.error;
        return_photo_url = upload.url;
      }
      return returnTool({
        assignment_id: assignment?.id,
        tool_id: assignment?.tool_id || assignment?.tool?.id,
        notes: notes || null,
        return_condition: returnCondition,
        return_photo_url
      });
    },
    onSuccess: ({ error: err } = {}) => {
      if (err) { setError(err.message); return; }
      toast.success(
        returnCondition === 'good'
          ? 'Herramienta devuelta.'
          : `Devuelta como ${returnCondition}. El admin fue notificado.`
      );
      invalidateAll();
      onHide();
    },
    onError: (e) => { setUploading(false); setError(e.message); }
  });

  // Marking a tool as lost requires typed-serial confirmation + reason
  // (per decision D-UX-97 — destructive actions are gated).
  //
  // `reportToolLost` hace las TRES cosas que la lista de consecuencias de
  // abajo promete. Antes acá se llamaba `updateToolCondition`, que sólo
  // cambiaba la condición: la asignación quedaba abierta y nadie se enteraba.
  const lostMutation = useMutation({
    mutationFn: ({ reason }) => reportToolLost({
      tool_id: assignment?.tool_id || assignment?.tool?.id,
      reason
    }),
    onSuccess: ({ error: err }) => {
      if (err) { setError(err.message); return; }
      toast.warning('Herramienta marcada como perdida.');
      invalidateAll();
      onHide();
    },
    onError: (e) => setError(e.message)
  });

  if (!assignment) return null;

  const isLost = mode === 'lost';
  const toolName = assignment.tool?.name || assignment._toolName || 'Herramienta';
  const toolSerial = assignment.tool?.serial_number || assignment._toolSerial || '';

  // Lost path: render the destructive confirm modal directly.
  if (isLost) {
    return (
      <DestructiveConfirmModal
        show={show}
        onHide={onHide}
        title="Marcar como perdida"
        icon="bi-exclamation-triangle"
        variant="danger"
        description={
          <>
            Vas a marcar <strong>{toolName}</strong>{' '}
            {toolSerial && <code className="ms-1">({toolSerial})</code>}{' '}
            como <strong>perdida</strong>.
          </>
        }
        consequences={[
          'Cambia la condición a "perdida" y la saca del inventario disponible.',
          'Cierra la asignación activa.',
          'Notifica al administrador y queda en el historial.',
          'Si la herramienta aparece después, debe re-registrarse o reactivarse manualmente.'
        ]}
        confirmText={toolSerial}
        confirmLabel="serial de la herramienta"
        reasonRequired
        reasonLabel="Circunstancias de la pérdida"
        reasonPlaceholder="Dónde y cuándo se perdió, qué se hizo para encontrarla..."
        submitLabel="Confirmar pérdida"
        onConfirm={({ reason }) => { setError(''); lostMutation.mutate({ reason }); }}
        pending={lostMutation.isPending}
        error={error || null}
      />
    );
  }

  // Normal return path: condition selector + photo (required when not good)
  const pending = returnMutation.isPending || uploading;
  return (
    <Modal show={show} onHide={onHide} centered fullscreen="sm-down">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-box-arrow-in-left text-success me-2" />
          Devolver herramienta
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <p className="mb-3">
          <strong>{toolName}</strong>{' '}
          {toolSerial && <span className="text-muted font-monospace">({toolSerial})</span>}
        </p>
        <Form.Group className="mb-3">
          <Form.Label>¿En qué condición la devolvés?</Form.Label>
          <Form.Select
            value={returnCondition}
            onChange={(e) => setReturnCondition(e.target.value)}
            disabled={pending}
          >
            {CONDITION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Form.Select>
        </Form.Group>

        {needsPhoto && (
          <Form.Group className="mb-3">
            <Form.Label>
              Foto del estado <span className="text-danger">*</span>
            </Form.Label>
            <ToolPhotoInput
              file={photoFile}
              previewUrl={photoPreview}
              onChange={(f, url) => { setPhotoFile(f); setPhotoPreview(url); }}
              onRemove={() => { setPhotoFile(null); setPhotoPreview(null); }}
              disabled={pending}
            />
            <Form.Text className="text-muted">
              Obligatoria porque reportaste condición distinta a "buen estado".
              Queda en el historial.
            </Form.Text>
          </Form.Group>
        )}

        <Form.Group>
          <Form.Label>Notas {needsPhoto ? '(qué pasó)' : '(opcional)'}</Form.Label>
          <Form.Control
            as="textarea" rows={2}
            value={notes} onChange={e => setNotes(e.target.value)}
            placeholder={
              needsPhoto
                ? 'Ej: "Se rompió la broca SDS-Plus, el motor funciona bien"'
                : 'Cualquier nota sobre el uso o devolución...'
            }
            disabled={pending}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={pending}>
          Cancelar
        </Button>
        <Button
          variant="success"
          disabled={pending || !photoOk}
          onClick={() => { setError(''); returnMutation.mutate(); }}
        >
          {pending && <Spinner size="sm" animation="border" className="me-1" />}
          {uploading ? 'Subiendo foto...' : 'Confirmar devolución'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ToolReturnModal;
